import { create } from 'zustand'
import { decodeJWT } from '@common/utils/validation/authValidators'
import {
  AuthTokenResponse,
  JwtPayloadCustom,
} from '@projtypes/token'
import {
  bootstrapSession,
  loginWithPassword,
  LogoutReason,
  registerUserWithToken,
  logoutSession,
  requestPasswordResetEmail,
  resetPasswordWithToken,
  refreshWithToken,
} from '@services/auth.service'
import {
  ACCESS_TOKEN_STORAGE_KEY,
  registerRefreshCallback,
} from '@api/backend'
import { queryClient } from '../lib/react-query/client'
import { useAccountStore } from './account.store'
import { useDeviceStore } from './device.store'
import { useLocationStore } from './location.store'
import {
  TokenGroup,
  tokenGroup,
  TokenPermission,
  tokenPermission,
  TokenRole,
  tokenRole,
} from '@data/rbac/model/rbac'

type AuthStatus =
  | 'idle'
  | 'bootstrapping'
  | 'logging-in'
  | 'refreshing'
  | 'authenticated'
  | 'anonymous'
  | 'error'

interface AuthStoreState {
  permissions: TokenPermission[]
  roles: TokenRole[]
  groups: TokenGroup[]
  loggingIn: boolean
  requestingPasswordReset: boolean
  resettingPassword: boolean
  passwordReset: boolean
  resetPasswordError: boolean
  registeringUser: boolean
  userRegistered: boolean
  registerUserError: boolean
  loading: boolean
  error: boolean
  authenticated: boolean
  loggedIn: boolean
  updating: boolean
  status: AuthStatus
}

interface AuthStoreActions {
  bootstrap: () => Promise<AuthTokenResponse | null>
  login: (email: string, password: string) => Promise<void>
  requestPasswordReset: (email: string) => Promise<void>
  resetPassword: (newPassword: string, verifyPassword: string, token: string) => Promise<void>
  registerUser: (params: RegisterUserParams) => Promise<void>
  resetAuthFormState: () => void
  refresh: () => Promise<AuthTokenResponse | null>
  logout: (reason?: LogoutReason) => Promise<void>
  applyToken: (token: AuthTokenResponse) => void
  clearSession: () => void
  setError: (value: boolean) => void
  hasPermissions: (requiredPermissions: string[]) => boolean
}

type AuthStore = AuthStoreState & AuthStoreActions

interface RegisterUserParams {
  firstname: string
  lastname: string
  mobile: string
  newPassword: string
  newPasswordConfirmation: string
  token: string
  uom: string
}

const initialState: AuthStoreState = {
  permissions: [],
  roles: [],
  groups: [],
  loggingIn: false,
  requestingPasswordReset: false,
  resettingPassword: false,
  passwordReset: false,
  resetPasswordError: false,
  registeringUser: false,
  userRegistered: false,
  registerUserError: false,
  loading: true,
  error: false,
  authenticated: false,
  loggedIn: false,
  updating: false,
  status: 'idle',
}

export const useAuthStore = create<AuthStore>()((set, get) => ({
  ...initialState,

  applyToken: (token: AuthTokenResponse) => {
    const decoded: JwtPayloadCustom = decodeJWT(token.access_token)
    set({
      permissions: decoded.permissions.map(tokenPermission),
      roles: decoded.roles.map(tokenRole),
      groups: decoded.groups.map(tokenGroup),
      loggingIn: false,
      loading: false,
      error: false,
      authenticated: true,
      loggedIn: true,
      updating: false,
      status: 'authenticated',
    })
  },

  clearSession: () => {
    useAccountStore.getState().reset()
    useDeviceStore.getState().reset()
    useLocationStore.getState().reset()

    // Clear everything, not an enumerated list of keys. Every cached response is scoped to
    // the session that fetched it, so anything left behind is visible to whoever logs in
    // next on the same browser. Enumerating accounts/devices/locations left tickets, water
    // usage, comments, billing, reports, alerts, and subscriptions in place — a HAR of
    // logout/login showed `/tickets` and `/tickets/summary` served from the previous
    // session's cache instead of refetched.
    //
    // A key list is the wrong shape for this: it has to be updated every time a query is
    // added, and forgetting is silent. Nothing legitimately needs to outlive a logout.
    queryClient.clear()

    set({ ...initialState, loading: false, status: 'anonymous' })
  },

  setError: (value: boolean) => {
    set((state) => ({
      error: value,
      loading: false,
      loggingIn: false,
      updating: false,
      status: value ? 'error' : state.status,
    }))
  },

  bootstrap: () => {
    set({ loading: true, status: 'bootstrapping' })
    const { applyToken, clearSession, setError } = get()
    return bootstrapSession({ applyToken, clearSession, setError })
  },

  login: (email: string, password: string) => {
    set({
      loggingIn: true,
      error: false,
      loading: false,
      status: 'logging-in',
    })
    const { applyToken, setError } = get()
    return loginWithPassword(email, password, { applyToken, setError })
  },

  requestPasswordReset: async (email: string) => {
    set({ requestingPasswordReset: true })
    try {
      await requestPasswordResetEmail(email)
    } finally {
      set({ requestingPasswordReset: false })
    }
  },

  resetPassword: async (newPassword: string, verifyPassword: string, token: string) => {
    set({
      resettingPassword: true,
      passwordReset: false,
      resetPasswordError: false,
    })

    try {
      await resetPasswordWithToken({ newPassword, verifyPassword, token })
      set({ passwordReset: true })
    } catch {
      set({ resetPasswordError: true })
    } finally {
      set({ resettingPassword: false })
    }
  },

  registerUser: async (params: RegisterUserParams) => {
    set({
      registeringUser: true,
      userRegistered: false,
      registerUserError: false,
    })

    try {
      await registerUserWithToken(params)
      set({ userRegistered: true })
    } catch {
      set({ registerUserError: true })
    } finally {
      set({ registeringUser: false })
    }
  },

  resetAuthFormState: () => {
    set({
      resettingPassword: false,
      passwordReset: false,
      resetPasswordError: false,
      registeringUser: false,
      userRegistered: false,
      registerUserError: false,
    })
  },

  refresh: () => {
    set({ updating: true, error: false, loading: false, status: 'refreshing' })
    const { applyToken, clearSession, setError } = get()
    return refreshWithToken({ applyToken, clearSession, setError })
  },

  logout: (reason = 'manual' as LogoutReason) => {
    const { clearSession } = get()
    return logoutSession({ clearSession }, reason)
  },

  hasPermissions: (requiredPermissions: string[]) => {
    const permissionNames = get().permissions.map((p) => p.name)
    return requiredPermissions.every((rp) => permissionNames.includes(rp))
  },
}))

registerRefreshCallback(() => useAuthStore.getState().refresh())

// localStorage is shared across windows but Zustand state is per-window: when
// another window ends the session (logout, failed refresh), this window must
// drop its in-memory session too or it stays mounted firing unauthenticated
// requests. 'storage' events only fire in windows other than the one writing.
window.addEventListener('storage', (event) => {
  if (event.key !== ACCESS_TOKEN_STORAGE_KEY || event.newValue !== null) {
    return
  }

  if (useAuthStore.getState().authenticated) {
    useAuthStore.getState().clearSession()
  }
})
